import { Modal } from '@/components';
import {
  useDeleteSitePhoto,
  useSitePhotosQuery,
} from '@/remote/site-management/site-photos';
import type { Site } from '@/types/site';
import { useState } from 'react';

interface SitePhotoModalProps {
  isOpen: boolean;
  site: Site | null;
  onClose: () => void;
  onUploadPhoto: () => void;
}

export default function SitePhotoModal({
  isOpen,
  site,
  onClose,
  onUploadPhoto,
}: SitePhotoModalProps) {
  const { data: photos = [], isLoading } = useSitePhotosQuery(
    site?.siteId || ''
  );
  const deletePhotoMutation = useDeleteSitePhoto();
  const [deletingPhotoId, setDeletingPhotoId] = useState<string | null>(null);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  const [enlargedPhoto, setEnlargedPhoto] = useState<string | null>(null);

  if (!site) return null;

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString('ko-KR');
  };

  const handleDeletePhoto = async (photoId: string) => {
    if (deletingPhotoId) return;

    setDeletingPhotoId(photoId);
    try {
      await deletePhotoMutation.mutateAsync({
        siteId: site.siteId,
        photoId,
      });
    } catch (error) {
      console.error('사진 삭제 실패:', error);
    } finally {
      setDeletingPhotoId(null);
    }
  };

  const handleEnlargePhoto = (photoId: string) => {
    setEnlargedPhoto(photoId);
  };

  const handleCloseEnlarged = () => {
    setEnlargedPhoto(null);
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={`${site.name} - 현장 사진 관리`}
        size="6xl"
        maxHeight="max-h-[90vh]"
        closeOnBackdropClick
        zIndex={999}
        headerActions={
          <button
            onClick={onUploadPhoto}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg cursor-pointer whitespace-nowrap flex items-center space-x-2"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-upload-line"></i>
            </div>
            <span>사진 업로드</span>
          </button>
        }
      >
        <div className="p-6">
          {isLoading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="text-gray-500 mt-2">사진 목록을 불러오는 중...</p>
            </div>
          ) : photos.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {photos.map((photo) => (
                <div
                  key={photo.id}
                  className="bg-gray-50 rounded-lg overflow-hidden"
                >
                  {failedImages.has(photo.id) ? (
                    <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
                      <i className="ri-image-line text-gray-400 text-4xl"></i>
                    </div>
                  ) : (
                    <img
                      src={`${import.meta.env.VITE_API_URL}/sites/${photo.siteId}/images/${photo.id}`}
                      alt={photo.title}
                      className="w-full h-48 object-cover cursor-pointer"
                      onError={() => {
                        setFailedImages((prev) => new Set([...prev, photo.id]));
                      }}
                      onClick={() => handleEnlargePhoto(photo.id)}
                    />
                  )}
                  <div className="p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">
                      {photo.title}
                    </h4>
                    <p className="text-xs text-gray-400 mb-3">
                      {formatDate(photo.createdTimestamp)}
                    </p>
                    <button
                      onClick={() => handleDeletePhoto(photo.id)}
                      disabled={deletingPhotoId === photo.id}
                      className={`text-red-600 hover:text-red-800 cursor-pointer text-sm flex items-center space-x-1 ${
                        deletingPhotoId === photo.id
                          ? 'opacity-50 cursor-not-allowed'
                          : ''
                      }`}
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        {deletingPhotoId === photo.id ? (
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-600"></div>
                        ) : (
                          <i className="ri-delete-bin-line"></i>
                        )}
                      </div>
                      <span>
                        {deletingPhotoId === photo.id ? '삭제 중...' : '삭제'}
                      </span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <div className="w-16 h-16 flex items-center justify-center bg-gray-200 rounded-full mx-auto mb-4">
                <i className="ri-camera-line text-gray-400 text-2xl"></i>
              </div>
              <p className="text-lg mb-2">업로드된 사진이 없습니다</p>
              <p className="text-sm">현장 사진을 업로드해 보세요.</p>
            </div>
          )}
        </div>
      </Modal>

      {/* 확대된 사진 모달 */}
      {enlargedPhoto && (
        <Modal
          isOpen={!!enlargedPhoto}
          onClose={handleCloseEnlarged}
          size="full"
          closeOnBackdropClick
          showCloseButton={true}
          zIndex={9999}
        >
          <div className="flex items-center justify-center h-full p-8">
            {photos.find((photo) => photo.id === enlargedPhoto) && (
              <img
                src={`${import.meta.env.VITE_API_URL}/sites/${site.siteId}/images/${enlargedPhoto}`}
                alt="확대된 사진"
                className="max-w-full max-h-full object-contain"
                onError={() => {
                  setFailedImages((prev) => new Set([...prev, enlargedPhoto]));
                  handleCloseEnlarged();
                }}
              />
            )}
          </div>
        </Modal>
      )}
    </>
  );
}
